/**
 * PARMAX — Barra spedizione gratuita nella scheda prodotto.
 * Markup e stato iniziale: snippets/parmax-free-shipping.liquid.
 *
 * Si aggiorna senza richieste di rete con gli eventi del tema:
 * - on:variant:change (variant-picker.js) -> variante selezionata
 * - change/input sul campo quantità del form prodotto
 * - on:cart:add / on:line-item:change (product-form.js / cart-items.js) -> carrello
 * Solo se il carrello cambia per altre vie (es. codice sconto nel carrello laterale, pagina
 * ripristinata dalla cache del browser) rilegge /cart.js.
 */
if (!customElements.get('parmax-free-shipping')) {
  class ParmaxFreeShipping extends HTMLElement {
    connectedCallback() {
      if (this.pfsReady) return;

      try {
        this.config = JSON.parse(this.querySelector('[data-pfs-config]').textContent);
      } catch (error) {
        return;
      }
      this.pfsReady = true;

      const cfg = this.config;
      this.cart = {
        total: Number(cfg.cartTotal) || 0,
        count: Number(cfg.cartCount) || 0,
        ids: (cfg.cartVariantIds || []).map(String)
      };
      this.variantId = cfg.variantId ? String(cfg.variantId) : null;
      this.lastCartUpdate = 0;

      this.msgEl = this.querySelector('.pfs__msg');
      this.cartFill = this.querySelector('.pfs__fill--cart');
      this.previewFill = this.querySelector('.pfs__fill--preview');
      this.blockEl = this.closest('.product-info__block');
      this.container = this.closest('.shopify-section') || document;
      this.qty = this.readQty();

      this.onVariantChange = this.handleVariantChange.bind(this);
      this.onCartData = this.handleCartData.bind(this);
      this.onQtyChange = this.handleQtyChange.bind(this);
      this.onQtyClick = this.handleQtyClick.bind(this);
      this.onPageshow = this.handlePageshow.bind(this);

      document.addEventListener('on:variant:change', this.onVariantChange);
      document.addEventListener('on:cart:add', this.onCartData);
      document.addEventListener('on:line-item:change', this.onCartData);
      document.addEventListener('change', this.onQtyChange);
      document.addEventListener('input', this.onQtyChange);
      document.addEventListener('click', this.onQtyClick);
      window.addEventListener('pageshow', this.onPageshow);

      this.observeDrawer();
      this.render();
    }

    disconnectedCallback() {
      if (!this.pfsReady) return;
      document.removeEventListener('on:variant:change', this.onVariantChange);
      document.removeEventListener('on:cart:add', this.onCartData);
      document.removeEventListener('on:line-item:change', this.onCartData);
      document.removeEventListener('change', this.onQtyChange);
      document.removeEventListener('input', this.onQtyChange);
      document.removeEventListener('click', this.onQtyClick);
      window.removeEventListener('pageshow', this.onPageshow);
      if (this.drawerObserver) this.drawerObserver.disconnect();
      clearTimeout(this.fetchTimer);
      this.pfsReady = false;
    }

    /**
     * Form prodotto principale della stessa sezione.
     * @returns {?Element}
     */
    getForm() {
      return this.container.querySelector('.js-product-form-main')
        || this.container.querySelector('.js-product-form');
    }

    /**
     * Quantità selezionata nel form prodotto (minimo 1).
     * @returns {number}
     */
    readQty() {
      const form = this.getForm();
      const input = form ? form.querySelector('[name="quantity"]') : null;
      const value = input ? parseInt(input.value, 10) : 1;
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    /**
     * @param {Event} evt - Evento on:variant:change.
     */
    handleVariantChange(evt) {
      if (this.container !== document && !this.container.contains(evt.target)) return;
      const variant = evt.detail ? evt.detail.variant : null;
      this.variantId = variant && variant.id ? String(variant.id) : null;
      this.qty = this.readQty();
      this.render();
    }

    /**
     * @param {Event} evt - Evento change/input.
     */
    handleQtyChange(evt) {
      const { target } = evt;
      if (!target || target.name !== 'quantity') return;
      const form = this.getForm();
      if (!form || !form.contains(target)) return;
      const qty = this.readQty();
      if (qty === this.qty) return;
      this.qty = qty;
      this.render();
    }

    /**
     * I pulsanti +/- del selettore quantità: rilegge il valore dopo l'aggiornamento.
     * @param {Event} evt - Evento click.
     */
    handleQtyClick(evt) {
      const btn = evt.target && evt.target.closest ? evt.target.closest('.qty-input__btn') : null;
      if (!btn) return;
      const form = this.getForm();
      if (!form || !form.contains(btn)) return;
      setTimeout(() => {
        const qty = this.readQty();
        if (qty === this.qty) return;
        this.qty = qty;
        this.render();
      }, 0);
    }

    /**
     * @param {Event} evt - Evento on:cart:add / on:line-item:change con detail.cart.
     */
    handleCartData(evt) {
      if (evt.detail && evt.detail.cart) this.updateCart(evt.detail.cart);
    }

    /**
     * @param {PageTransitionEvent} evt - Evento pageshow.
     */
    handlePageshow(evt) {
      if (evt.persisted) this.fetchCart(true);
    }

    /**
     * @param {object} cart - Oggetto carrello (formato /cart.js).
     */
    updateCart(cart) {
      if (!cart || typeof cart.total_price === 'undefined') return;
      this.cart = {
        total: Number(cart.total_price) || 0,
        count: Number(cart.item_count) || 0,
        ids: (cart.items || []).map((item) => String(item.variant_id))
      };
      this.lastCartUpdate = Date.now();
      this.qty = this.readQty();
      this.render();
    }

    /**
     * Rilegge il carrello (solo quando cambia senza eventi del tema).
     * @param {boolean} [force=false] - Ignora l'ultimo aggiornamento ricevuto via evento.
     */
    fetchCart(force = false) {
      if (!force && Date.now() - this.lastCartUpdate < 2000) return;
      const cartRoute = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
      fetch(`${cartRoute}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .then((cart) => { if (cart) this.updateCart(cart); })
        .catch(() => {});
    }

    /**
     * Osserva la barra del carrello laterale: se viene ridisegnata senza un evento
     * (es. codice sconto applicato), rilegge il carrello.
     */
    observeDrawer() {
      const drawer = document.getElementById('cart-drawer');
      if (!drawer || typeof MutationObserver === 'undefined') return;
      this.drawerObserver = new MutationObserver((mutations) => {
        const relevant = mutations.some((m) => m.target === drawer
          || (m.target.closest && m.target.closest('#free-shipping-notice')));
        if (!relevant) return;
        clearTimeout(this.fetchTimer);
        this.fetchTimer = setTimeout(() => this.fetchCart(), 300);
      });
      this.drawerObserver.observe(drawer, { childList: true, subtree: true });
    }

    /**
     * Calcola lo stato e aggiorna testo e barra (stessa logica dello snippet Liquid).
     */
    render() {
      const cfg = this.config;
      const { texts } = cfg;
      const min = Number(cfg.min) || 0;
      if (min <= 0) return;

      const variantData = this.variantId && cfg.variants ? cfg.variants[this.variantId] : null;
      const inCart = this.variantId ? this.cart.ids.includes(this.variantId) : false;
      const canPreview = Boolean(cfg.enablePreview && variantData && variantData[1] && !inCart);

      let state;
      let total;
      let msg;

      if (canPreview) {
        total = this.cart.total + (Number(variantData[0]) * this.qty);
        if (total >= min) {
          state = 'preview-free';
          msg = texts.previewFree;
        } else {
          state = 'preview-deficit';
          msg = (texts.previewDeficit || '').split('[importo]').join(this.formatMoney(min - total));
        }
      } else if (this.cart.count > 0) {
        total = this.cart.total;
        if (total >= min) {
          state = 'free';
          msg = texts.free;
        } else {
          state = 'deficit';
          msg = (texts.deficit || '').split('[importo]').join(this.formatMoney(min - total));
        }
      } else {
        total = 0;
        state = 'empty';
        msg = texts.empty;
      }

      const hidden = state === 'empty' && !texts.empty;
      this.hidden = hidden;
      if (this.blockEl) this.blockEl.hidden = hidden;

      this.dataset.state = state;
      if (this.msgEl && this.msgEl.innerHTML !== (msg || '')) this.msgEl.innerHTML = msg || '';
      if (this.cartFill) this.cartFill.style.width = `${ParmaxFreeShipping.pct(this.cart.total, min)}%`;
      if (this.previewFill) this.previewFill.style.width = `${ParmaxFreeShipping.pct(total, min)}%`;
    }

    /**
     * @param {number} value - Importo in centesimi.
     * @param {number} min - Soglia in centesimi.
     * @returns {number} Percentuale 0-100.
     */
    static pct(value, min) {
      return Math.max(0, Math.min(100, (value * 100) / min));
    }

    /**
     * Formatta i centesimi con il formato valuta del negozio (come il filtro Liquid money).
     * @param {number} cents - Importo in centesimi.
     * @returns {string}
     */
    formatMoney(cents) {
      const format = this.config.moneyFormat || '€{{amount_with_comma_separator}}';
      const value = Math.round(Number(cents) || 0);
      const withDelimiters = (precision, thousands, decimal) => {
        const parts = (value / 100).toFixed(precision).split('.');
        const integer = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
        return parts[1] ? `${integer}${decimal}${parts[1]}` : integer;
      };
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
        switch (key) {
          case 'amount_no_decimals': return withDelimiters(0, ',', '.');
          case 'amount_with_comma_separator': return withDelimiters(2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return withDelimiters(0, '.', ',');
          case 'amount_with_apostrophe_separator': return withDelimiters(2, "'", '.');
          case 'amount_with_space_separator': return withDelimiters(2, ' ', ',');
          case 'amount_no_decimals_with_space_separator': return withDelimiters(0, ' ', ',');
          case 'amount_with_period_and_space_separator': return withDelimiters(2, ' ', '.');
          default: return withDelimiters(2, ',', '.');
        }
      });
    }
  }

  customElements.define('parmax-free-shipping', ParmaxFreeShipping);
}
